/* ============================================================================
   LUNCH-B  ·  interactions
   Figma: JVS6kkMecptvsB7xl4MnH2

   Everything here is driven off design-px coordinates read back from the live
   artboard, so a layer's motion stays locked to its Figma placement at any
   viewport width.
   ========================================================================= */
(function () {
  "use strict";
  if (window.__lbLoaded) return;           // the asset is included by every section
  window.__lbLoaded = true;

  var reduce = window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* WebKit runs the plate turn visibly slower than Blink at the same declared
     duration — reported side by side, iPhone against Android and Windows. The
     class is the only thing decided here; the CSS reads it as --lb-turnf: .5,
     which is half the seconds, i.e. twice the speed. Set at parse time (the
     asset is deferred, so this still lands before first paint) so no plate is
     ever seen turning at the wrong rate.
     iPadOS 13+ reports itself as Macintosh, hence the touch-point test. */
  var _ua = navigator.userAgent || "";
  var isIOS = /iP(hone|od|ad)/.test(_ua) ||
              (/Macintosh/.test(_ua) && (navigator.maxTouchPoints || 0) > 1);
  var isSafari = /Safari/.test(_ua) &&
                 !/Chrome|Chromium|CriOS|FxiOS|Edg|EdgiOS|OPR|OPiOS|Android/.test(_ua);
  if (isIOS || isSafari) document.documentElement.classList.add("lb-webkit");

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }
  function each(list, fn) { Array.prototype.forEach.call(list, fn); }

  /* Design-px scale of an artboard: 1 design px == `unit` CSS px right now.
     There is ONE composition now — the 1080-wide artboard, at every viewport —
     so the divisor is always 1080. The desktop canvas is retired: its media
     query is pinned past any real display (restore 1700px in the CSS, the JS
     below and lb-theme.liquid's img sizes to bring it back), which leaves this
     branch permanently false rather than deleting the wiring. */
  var WIDE = window.matchMedia("(min-width: 20000px)");
  function isWide() { return WIDE.matches; }

  function unitOf(art) {
    var r = art.getBoundingClientRect();
    var aw = 1080;
    if (isWide()) {
      aw = parseFloat(getComputedStyle(art).getPropertyValue("--daw")) || 1920;
    }
    return { u: r.width / aw, top: r.top, rect: r };
  }

  /* A tap that actually fires on a phone.

     `click` alone is not enough here: on iOS the page is nearly always still
     settling — momentum scroll, images landing, the theme cross-fading — and a
     touch that drifts a few pixels while any of that is happening is treated
     as a scroll gesture and the click is never delivered. That is exactly the
     "tap the sandwich and nothing happens" report. So: resolve on touch/pointer
     release when the finger stayed put and the press was short, and swallow the
     synthetic click that follows so nothing fires twice. */
  function onTap(el, fn) {
    var sx = 0, sy = 0, st = 0, armed = false, last = 0;

    function fire(e) {
      var now = Date.now();
      if (now - last < 400) return;      // the click chaser after a touch
      last = now;
      fn(e);
    }
    function down(e) {
      var p = e.touches ? e.touches[0] : e;
      sx = p.clientX; sy = p.clientY; st = Date.now(); armed = true;
    }
    function up(e) {
      if (!armed) return;
      armed = false;
      var p = e.changedTouches ? e.changedTouches[0] : e;
      if (Date.now() - st > 700) return;                       // a long press
      if (Math.abs(p.clientX - sx) > 12 || Math.abs(p.clientY - sy) > 12) return;
      fire(e);
    }
    if (window.PointerEvent) {
      el.addEventListener("pointerdown", down, { passive: true });
      el.addEventListener("pointerup", up, { passive: true });
      el.addEventListener("pointercancel", function () { armed = false; });
    } else {
      el.addEventListener("touchstart", down, { passive: true });
      el.addEventListener("touchend", up, { passive: true });
      el.addEventListener("touchcancel", function () { armed = false; });
    }
    el.addEventListener("click", fire);
    el.addEventListener("keydown", function (e) {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fn(e); }
    });
  }

  /* a pivot/anchor in design px, for whichever composition is showing */
  function anchorY(el) {
    var v = isWide() ? el.getAttribute("data-py-d") : el.getAttribute("data-py");
    return parseFloat(v) || parseFloat(el.getAttribute("data-py")) || 0;
  }

  /* ------------------------------------------------------------------ *
   * 0. Neutralise the storefront theme's opaque section wrappers
   * ------------------------------------------------------------------ */
  function initHosts() {
    each(document.querySelectorAll(".lb-page, .lb-switch-host"), function (el) {
      var host = el.closest && el.closest(".shopify-section");
      if (host) host.classList.add("lb-host");
    });
  }

  /* ------------------------------------------------------------------ *
   * 1. Theme switch
   *    Both artboards occupy the same space (the casual one carries a
   *    negative top margin of a full artboard), so the document height
   *    never changes and the reader keeps their scroll position.
   * ------------------------------------------------------------------ */
  var currentTheme = "formal";
  var themeListeners = [];
  function onTheme(fn) { themeListeners.push(fn); }

  function hydrate(page) {
    /* The non-default theme ships without src= so its artwork costs nothing
       until someone actually asks for it. */
    each(page.querySelectorAll("img[data-lb-src]"), function (img) {
      var ss = img.getAttribute("data-lb-srcset");
      if (ss) { img.srcset = ss; img.removeAttribute("data-lb-srcset"); }
      img.src = img.getAttribute("data-lb-src");
      img.removeAttribute("data-lb-src");
    });
    each(page.querySelectorAll("[data-lb-bgsrc]"), function (el) {
      el.style.setProperty("--src", el.getAttribute("data-lb-bgsrc"));
      el.removeAttribute("data-lb-bgsrc");
    });
  }

  function setTheme(name) {
    if (name === currentTheme) return;
    currentTheme = name;
    document.documentElement.setAttribute("data-lb-theme", name);
    each(document.querySelectorAll(".lb-page[data-lb-variant]"), function (page) {
      var active = page.getAttribute("data-lb-variant") === name;
      if (active) hydrate(page);
      page.setAttribute("data-lb-active", active ? "true" : "false");
      page.setAttribute("aria-hidden", active ? "false" : "true");
    });
    each(document.querySelectorAll("[data-lb-switch]"), function (btn) {
      btn.setAttribute("aria-pressed", name === "casual" ? "true" : "false");
      btn.setAttribute("aria-label",
        name === "casual" ? "Switch to the formal menu" : "Switch to the casual menu");
    });
    themeListeners.forEach(function (fn) { try { fn(name); } catch (e) {} });
  }

  function initSwitch() {
    var host = document.querySelector(".lb-switch-host");
    var btns = document.querySelectorAll("[data-lb-switch]");
    if (!btns.length) return;

    /* A transformed / filtered ancestor silently turns position:fixed into
       position:absolute. If that has happened, move the switch to <body> so it
       really does ride the viewport. */
    if (host) {
      var p = host.parentElement, broken = false;
      while (p && p !== document.body) {
        var cs = getComputedStyle(p);
        if (cs.transform !== "none" || cs.perspective !== "none" ||
            cs.filter !== "none" || /paint|layout|strict|content/.test(cs.contain || "")) {
          broken = true; break;
        }
        p = p.parentElement;
      }
      if (broken) document.body.appendChild(host);
    }

    each(btns, function (btn) {
      btn.addEventListener("click", function () {
        setTheme(currentTheme === "formal" ? "casual" : "formal");
      });
    });

    /* The pill is position:fixed, but a fixed top offset lands it on the
       storefront header whenever the header stack is taller than the offset
       — reported on several screens. Track the first artboard's top edge and
       feed it to the CSS max(): while the artboard's top is on screen the
       pill stays 18px inside the page, and once scrolled past, the fixed
       offset wins and it pins to the viewport as before. */
    var firstPage = document.querySelector(".lb-page");
    if (host && firstPage) {
      var clampTick = false;
      var clampNow = function () {
        clampTick = false;
        host.style.setProperty("--lb-clamp",
          (firstPage.getBoundingClientRect().top + 18) + "px");
      };
      var onClamp = function () {
        if (clampTick) return;
        clampTick = true;
        requestAnimationFrame(clampNow);
      };
      window.addEventListener("scroll", onClamp, { passive: true });
      window.addEventListener("resize", onClamp, { passive: true });
      clampNow();
    }

    /* establish the initial state without animating it */
    var first = document.querySelector('.lb-page[data-lb-default="true"]');
    currentTheme = first ? first.getAttribute("data-lb-variant") : "formal";

    /* Deep link: #casual / ?look=casual lands straight on the casual board
       (and #formal / ?look=formal forces formal), so each side of the page
       gets its own shareable URL. The bare URL keeps the default. */
    try {
      var wanted = (location.hash || "").slice(1).toLowerCase();
      if (wanted !== "casual" && wanted !== "formal") {
        wanted = (new URLSearchParams(location.search).get("look") || "").toLowerCase();
      }
      if (wanted === "casual" || wanted === "formal") currentTheme = wanted;
    } catch (e) {}
    document.documentElement.setAttribute("data-lb-theme", currentTheme);
    each(document.querySelectorAll(".lb-page[data-lb-variant]"), function (page) {
      var active = page.getAttribute("data-lb-variant") === currentTheme;
      page.setAttribute("data-lb-active", active ? "true" : "false");
      page.setAttribute("aria-hidden", active ? "false" : "true");
      if (active) hydrate(page);
    });
    each(btns, function (btn) {
      btn.setAttribute("aria-pressed", currentTheme === "casual" ? "true" : "false");
      btn.setAttribute("aria-label",
        currentTheme === "casual" ? "Switch to the formal menu" : "Switch to the casual menu");
    });
  }

  /* ------------------------------------------------------------------ *
   * 2. Scroll-driven rotation
   *    The badge ring and the hero prop only. The PLATES are deliberately
   *    absent: they turn continuously on the .lb-turn animation and have no
   *    relationship to scroll at all.
   * ------------------------------------------------------------------ */
  function initSpin() {
    var rings = document.querySelectorAll("[data-lb-ring]");
    var orbits = document.querySelectorAll("[data-lb-orbit]");
    if (!rings.length && !orbits.length) return;

    /* Reduced motion shortens the turn rather than cancelling it. Bailing out
       here left every plate frozen at its Figma pose, which looks like the
       page is broken — and iOS ships Reduce Motion on far more phones than
       desktop does. A third of the sweep still tracks the scroll and is
       nowhere near enough to be uncomfortable. */
    var damp = reduce ? 0.3 : 1;

    function update() {
      var vh = window.innerHeight || 1;

      /* Both scroll rotations anchor ZERO to the pose the page loaded in —
         the Aug-30 design PDFs show the badge at its drawn angle ON LOAD
         (previously the ring zeroed at progress 0 and the prop at
         mid-screen, so the load view never matched the artboard). */
      each(rings, function (el) {
        var art = el.closest(".lb-art"); if (!art) return;
        var m = unitOf(art);
        var r = el.getBoundingClientRect();
        var turns = (parseFloat(el.getAttribute("data-turns")) || 1) * damp;
        var progress = 1 - (r.top + r.height / 2) / vh;
        progress = Math.max(-0.35, Math.min(1.35, progress));
        var p0 = parseFloat(el.getAttribute("data-lb-p0"));
        if (isNaN(p0)) { p0 = progress; el.setAttribute("data-lb-p0", p0.toFixed(4)); }
        el.style.setProperty("--lb-ring-deg", ((progress - p0) * turns * 360).toFixed(2));
      });

      /* The prop turns on the spot, about its own centre. */
      each(orbits, function (el) {
        var art = el.closest(".lb-art"); if (!art) return;
        var m = unitOf(art);
        var py = anchorY(el);
        var turns = (parseFloat(el.getAttribute("data-turns")) || 0.5) * damp;
        var y = m.top + py * m.u;
        var progress = 1 - y / vh;
        progress = Math.max(-0.5, Math.min(1.5, progress));
        var p0 = parseFloat(el.getAttribute("data-lb-p0"));
        if (isNaN(p0)) { p0 = progress; el.setAttribute("data-lb-p0", p0.toFixed(4)); }
        el.style.setProperty("--lb-orbit", ((progress - p0) * turns * 360).toFixed(2) + "deg");
      });
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { update(); ticking = false; });
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    if (WIDE.addEventListener) WIDE.addEventListener("change", onScroll);
    onTheme(onScroll);
    update();
  }

  /* ------------------------------------------------------------------ *
   * 3. The olive skewer: tap for one decelerating turn
   * ------------------------------------------------------------------ */
  function initSkewer() {
    each(document.querySelectorAll(".lb-skewer"), function (el) {
      var hit = el.closest(".lb-l") || el;
      /* the turn goes on the wrapper: the image itself carries the prop's own
         designed angle, which a spin on the image would overwrite */
      var spinner = el.closest(".lb-tapspin") || hit;
      hit.classList.add("lb-l--tap");
      hit.setAttribute("role", "button");
      hit.setAttribute("tabindex", "0");
      hit.setAttribute("aria-label", "Spin");
      /* Deliberately NOT gated on reduced motion: this turn only ever happens
         because someone tapped the prop asking for it. The CSS shortens it
         instead. */
      var timer = 0;
      function release() {
        clearTimeout(timer);
        spinner.classList.remove("is-spinning");
      }
      function spin() {
        if (spinner.classList.contains("is-spinning")) return;
        spinner.classList.add("is-spinning");
        /* animationend is the normal release, but it does not always arrive:
           a document that is hidden when the class lands never starts the
           animation at all, so no end event is ever fired and the prop stays
           latched for the rest of the visit — reproduced, not theorised. The
           timer is the floor. It is set past the longest dwell (5.4s), so on
           every normal spin animationend still wins and this never fires. */
        timer = setTimeout(release, 7000);
      }
      spinner.addEventListener("animationend", release);
      onTap(hit, spin);

      /* Not only a tap reward: the skewer takes one turn whenever its artboard
         becomes the one being read — once on load, and again on every switch
         back to it (2026-08-19 request). The theme callback fires only on a
         real change, so the initial state has to be handled separately.
         The delays let the arrival reveal land first; spinning a prop that is
         still sliding into place reads as a glitch rather than a flourish. */
      var page = el.closest(".lb-page");
      var variant = page ? page.getAttribute("data-lb-variant") : "formal";
      onTheme(function (name) { if (name === variant) setTimeout(spin, 260); });
      if (currentTheme === variant) setTimeout(spin, 700);
    });
  }

  /* ------------------------------------------------------------------ *
   * 3b. Tap the hero to reach the first product
   *    Above the first plate there is nothing to do but read, and on a phone
   *    the first product is several thumb-drags away. A tap anywhere in that
   *    band scrolls to it. The props that already answer a tap — the skewer,
   *    the sandwich, the envelope, any link or the switch — keep their own
   *    behaviour; this is the empty space talking, nothing else.
   *
   *    The band is measured in DESIGN px down the artboard, not in viewport
   *    px, so it means the same thing at every width: everything above the
   *    first plate group's pivot, less that plate's own reach.
   * ------------------------------------------------------------------ */
  var SKIP_TAP = ".lb-l--tap, .lb-stack, .lb-stack__hit, .lb-tapspin, .lb-cdstage," +
                 " a, button, [data-lb-switch], [role='button']";

  function initHeroTap() {
    each(document.querySelectorAll(".lb-page[data-lb-variant]"), function (page) {
      var grp1 = page.querySelector('[data-lb-grp="1"]');
      /* MEASURE THE ARTBOARD, NOT THE PAGE. .lb-page is a full-width wrapper;
         .lb-art is the 1080-unit artboard, and it stops growing once --u hits
         its 0.68px cap (~734px viewport). Below the cap the two are the same
         width, which is why measuring the wrapper looked correct on a phone
         and silently mis-scaled every tap on a tablet or desktop. */
      var art = page.querySelector(".lb-art");
      if (!grp1 || !art) return;
      var pivotY = parseFloat(grp1.getAttribute("data-py"));
      if (!pivotY) return;
      /* the plate's own reach, so the band stops short of the composition
         rather than running under it */
      var bandEnd = pivotY - 560;

      onTap(page, function (e) {
        if (page.getAttribute("data-lb-active") !== "true") return;
        var t = e.target;
        if (t && t.closest && t.closest(SKIP_TAP)) return;
        var m = unitOf(art);
        var p = (e.changedTouches && e.changedTouches[0]) || e;
        var cy = p.clientY, cx = p.clientX;
        if (cy === undefined || cy === null) return;
        /* the gutter beside the artboard on a wide screen is not the hero */
        if (cx < m.rect.left || cx > m.rect.right) return;
        var y = (cy - m.rect.top) / m.u;
        if (y < 0 || y > bandEnd) return;
        var to = m.rect.top + window.pageYOffset + pivotY * m.u -
                 (window.innerHeight || 0) / 2;   /* land the plate mid-screen */
        if (to < 0) to = 0;
        try { window.scrollTo({ top: to, behavior: "smooth" }); }
        catch (err) { window.scrollTo(0, to); }   /* no options-object support */
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 4. The sandwich: deconstructed, then stacked bottom-up
   *    Replays every time the casual theme is brought in.
   * ------------------------------------------------------------------ */
  function initStack() {
    var stacks = document.querySelectorAll(".lb-stack");
    if (!stacks.length) return;

    function play(stack) {
      /* Removing is-stacked STARTS A 1.44s TRANSITION toward the exploded
         pose — re-adding the class 60ms later just turned that around mid-
         flight, so a tap produced a barely visible jiggle instead of a
         rebuild. The slices must JUMP to the exploded pose with transitions
         off, and only then stack. */
      stack.classList.add("is-resetting");
      stack.classList.remove("is-stacked");
      void stack.offsetWidth;                  // commit the jump, no transition
      stack.classList.remove("is-resetting");
      void stack.offsetWidth;                  // transitions are live again
      /* rAF alone is not safe here: a throttled or unpainted tab drops it and
         the sandwich would stay permanently deconstructed. Whichever of the
         two fires first wins; `done` keeps it to one. */
      var done = false;
      function go() {
        if (done) return;
        done = true;
        stack.classList.add("is-stacked");
      }
      requestAnimationFrame(function () { requestAnimationFrame(go); });
      setTimeout(go, 90);
    }

    each(stacks, function (stack) {
      var page = stack.closest(".lb-page");
      var variant = page ? page.getAttribute("data-lb-variant") : null;
      if (!variant || variant === currentTheme) play(stack);
      onTheme(function (name) { if (name === variant) play(stack); });

      /* tapping the sandwich rebuilds it */
      var hit = stack.querySelector(".lb-stack__hit");
      if (hit) onTap(hit, function () { play(stack); });
    });
  }

  /* ------------------------------------------------------------------ *
   * 5. Envelope: the knife travels in, the flap lifts, the letter rises,
   *    then the knife goes back where it was.
   * ------------------------------------------------------------------ */
  /* The envelope is the Claude Design component now (end of this file); the
     old layer-based envelope and its driver are gone. */

  /* ------------------------------------------------------------------ *
   * 6. The plated turn — with a safety net
   *    The turn is a CSS animation, which is where it belongs. But this has
   *    now been reported dead on an iPhone twice, and WebKit is not something
   *    that can be tested from here, so: sample the animation once shortly
   *    after load and, if it has not actually advanced, drive the same
   *    rotation from rAF instead. Costs one timeout when everything is fine.
   * ------------------------------------------------------------------ */
  function initTurn() {
    var turns = document.querySelectorAll("[data-lb-turn]");
    if (!turns.length || reduce) return;

    function seconds(el) {
      var cs = parseFloat(getComputedStyle(el).animationDuration);
      if (cs > 0) return cs;
      return parseFloat(el.style.getPropertyValue("--turn")) || 14.5;
    }

    var probeTries = 0;
    function probeTurns() {
      /* The test is whether the animation EXISTS, not whether its clock has
         moved. A stalled clock is normal — a background tab, a throttled
         renderer — and cancelling a healthy animation for that would trade a
         working turn for a starved rAF loop. No animation object at all is
         the real signature of a declaration the engine refused to parse.
         The probe must be an AWAKE turn: on WebKit the sleeper strips the
         animation off .lb-asleep elements entirely, so a sleeping probe has
         no animation object by DESIGN. At load every plate can be asleep
         (the page opens on the hero) — keep re-arming until one wakes, and
         judge the engine only on that one. */
      var probe = null;
      for (var i = 0; i < turns.length; i++) {
        if (!turns[i].classList.contains("lb-asleep")) { probe = turns[i]; break; }
      }
      if (!probe) {
        if (probeTries++ < 40) setTimeout(probeTurns, 1400);
        return;
      }
      if (!probe.getAnimations || probe.getAnimations().length) return;

      var start = null;
      requestAnimationFrame(function step(ts) {
        if (start === null) start = ts;
        each(turns, function (t) {
          /* honour the sleeper here too: a decoration nobody can see is not
             worth a style write per frame */
          if (t.classList.contains("lb-asleep")) return;
          var dur = (parseFloat(t.getAttribute("data-turn-secs")) || 14.5) * 1000;
          var deg = ((ts - start) / dur * 360) % 360;
          t.style.transform = "rotate(" + deg.toFixed(2) + "deg)";
        });
        requestAnimationFrame(step);
      });
    }
    setTimeout(probeTurns, 1400);

    each(turns, function (t) { t.setAttribute("data-turn-secs", seconds(t)); });
  }

  /* ------------------------------------------------------------------ *
   * 7. Product colourways
   *    On its own clock, independent of the turn — the colour changes every
   *    few seconds whatever pose the plate is in.
   * ------------------------------------------------------------------ */
  function initCycle() {
    each(document.querySelectorAll(".lb-l--cyc"), function (box) {
      var imgs = box.querySelectorAll("img");
      if (imgs.length < 2) return;             // one colourway: nothing to cycle
      var secs = parseFloat(box.getAttribute("data-cycle")) || 3;
      var i = 0;
      var page = box.closest(".lb-page");
      setInterval(function () {
        /* a hidden theme has no reader: cycling it just spends their data
           downloading colourways they may never switch to see */
        if (page && page.getAttribute("data-lb-active") !== "true") return;
        imgs[i].classList.remove("is-on");
        i = (i + 1) % imgs.length;
        var next = imgs[i];
        /* the alternates ship without src= like the inactive theme does, so
           the first cycle is what pays for them */
        if (next.getAttribute("data-lb-src")) {
          /* srcset FIRST: setting src alone commits the fallback 2x cut
             before the browser ever sees the candidate list, and the casual
             colourways were the only images still arriving at full size */
          var ss = next.getAttribute("data-lb-srcset");
          if (ss) { next.srcset = ss; next.removeAttribute("data-lb-srcset"); }
          next.src = next.getAttribute("data-lb-src");
          next.removeAttribute("data-lb-src");
        }
        next.classList.add("is-on");
      }, Math.max(1, secs) * 1000);
    });
  }

  /* ------------------------------------------------------------------ *
   * 7. Scroll reveal
   *    Layers are routinely wider than the viewport and bleed past the
   *    frame, so an area-ratio threshold can never be satisfied for them
   *    and they would sit at opacity 0 forever. Fire on ANY intersection,
   *    and keep a slow interval sweep as a backstop for the callbacks a
   *    throttled tab drops.
   * ------------------------------------------------------------------ */
  function initReveal() {
    var els = Array.prototype.slice.call(document.querySelectorAll(".lb-anim"));
    if (!els.length) return;
    if (reduce || !("IntersectionObserver" in window)) {
      els.forEach(function (e) { e.classList.add("is-in"); });
      return;
    }
    var pending = els.slice();
    function show(el) {
      el.classList.add("is-in");
      var i = pending.indexOf(el);
      if (i > -1) pending.splice(i, 1);
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { show(en.target); io.unobserve(en.target); }
      });
    }, { threshold: 0, rootMargin: "0px 0px -6% 0px" });
    els.forEach(function (e) { io.observe(e); });

    var last = 0, timer = null;
    function sweep() {
      var vh = window.innerHeight || 1;
      pending.slice().forEach(function (el) {
        if (el.getBoundingClientRect().top < vh * 0.94) { show(el); io.unobserve(el); }
      });
      if (!pending.length) {
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onScroll);
        if (timer) clearInterval(timer);
      }
    }
    function onScroll() {
      var now = Date.now();
      if (now - last < 90) return;
      last = now;
      sweep();
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    timer = setInterval(sweep, 700);
    sweep();
  }

  /* ------------------------------------------------------------------ *
   * 8. Ambient animation sleeper
   *    The decorative loops (plate turns, floats, the hero hover) ran on
   *    every layer of the page at all times — offscreen ones included. A
   *    phone GPU pays for each of those composited layers every frame, so
   *    an IntersectionObserver puts loops to sleep while their layer is
   *    away from the viewport.
   *
   *    ONE judge, for everything: a generous IO band around the viewport.
   *    The plates used to have a second, stricter test — turn only while the
   *    plate's centre sits in the middle 40% of the screen — which was asked
   *    for and then asked back out again (2026-08-19): a plate that stops the
   *    moment it leaves the middle of the screen reads as broken, not as
   *    restful. Plates now turn continuously for as long as they are anywhere
   *    near the viewport, exactly like the floats. Offscreen is still
   *    offscreen: the 45% margin means a plate is already turning well before
   *    it arrives and keeps turning well after it leaves, so nothing a reader
   *    can see is ever still. The colourway cycle is not part of this at all
   *    — it runs on its own interval whatever the pose.
   *
   *    Plate groups are rigid bodies: plate and product turn on separate
   *    .lb-turn wrappers whose clocks must stay identical, and a pause
   *    shifts a clock. Members of a group therefore sleep and wake as one
   *    unit, in the same callback, so their clocks always shift by the
   *    same amount and the product can never drift off its plate.
   *
   *    The envelope component (.lb-cdstage) is excluded: its animations
   *    are a tap-driven state machine with JS timers, and pausing those
   *    mid-flight would desync states from poses.
   * ------------------------------------------------------------------ */
  function initSleep() {
    if (!("IntersectionObserver" in window) || typeof Map === "undefined") return;
    var all = Array.prototype.filter.call(
      document.querySelectorAll(".lb-turn, .lb-float, .lb-float--alt, .lb-hover, .lb-stack"),
      function (el) { return !el.closest(".lb-cdstage"); }
    );
    if (!all.length) return;

    /* one unit per rigid group (keyed per theme), one per loose element */
    var units = [], byKey = {};
    var boxMap = new Map();
    all.forEach(function (el) {
      var unit = null;
      if (el.classList.contains("lb-turn")) {
        var spin = el.closest("[data-lb-grp]");
        var page = el.closest(".lb-page");
        if (spin) {
          var key = (page ? page.getAttribute("data-lb-variant") : "") +
                    "#" + spin.getAttribute("data-lb-grp");
          unit = byKey[key];
          if (!unit) { unit = { els: [], visible: new Set() }; byKey[key] = unit; units.push(unit); }
        }
      }
      if (!unit) { unit = { els: [], visible: new Set() }; units.push(unit); }
      unit.els.push(el);
      /* observe the sized layer box, not the wrapper — the wrappers are
         deliberately zero-sized and a zero-area target makes intersection
         reporting unreliable */
      var box = el.classList.contains("lb-l") ? el
              : (el.querySelector(".lb-l") || el.closest(".lb-l") || el);
      (unit.boxes = unit.boxes || []).push(box);
    });

    /* Which of a unit's boxes get to WAKE it. On WebKit the fx shadow boxes
       are excluded when the unit has anything else: they are soft, mostly
       transparent skirts up to twice the plate's size, and on a phone their
       padding reaches the wake band while the plate itself is a full screen
       away — which kept every group's GPU textures alive at once (the
       memory that killed the tab). The shadow still turns rigidly with its
       group; it just doesn't get a vote on when the group wakes. A shadow
       that is a unit's ONLY box (a loose float) keeps its vote. */
    var wkSkipFx = document.documentElement.classList.contains("lb-webkit");
    units.forEach(function (u) {
      var watch = u.boxes;
      if (wkSkipFx && u.boxes.length > 1) {
        var nonFx = u.boxes.filter(function (b) {
          var img = b.querySelector && b.querySelector("img");
          var src = img && (img.currentSrc || img.getAttribute("src") ||
                            img.getAttribute("data-lb-src") || "");
          return !(src && src.indexOf("lb-fx-") > -1);
        });
        if (nonFx.length) watch = nonFx;
      }
      watch.forEach(function (b) {
        var list = boxMap.get(b);
        if (!list) { list = []; boxMap.set(b, list); }
        if (list.indexOf(u) < 0) list.push(u);
      });
    });

    /* any overlap with a generous band around the viewport */
    var ioWide = new IntersectionObserver(function (entries) {
      var touched = new Set();
      entries.forEach(function (en) {
        (boxMap.get(en.target) || []).forEach(function (u) {
          if (en.isIntersecting) u.visible.add(en.target);
          else u.visible.delete(en.target);
          touched.add(u);
        });
      });
      /* whole unit flips in one pass = one style flush = clocks stay lockstep */
      touched.forEach(function (u) {
        var asleep = u.visible.size === 0;
        u.els.forEach(function (el) { el.classList.toggle("lb-asleep", asleep); });
      });
    /* WebKit gets a viewport-hugging band: every awake loop is a composited
       GPU texture there (see the .lb-webkit rules in the CSS), and the wide
       45% apron kept 2-3 whole spin groups' textures alive at once — which
       is what evicted layers and crashed the tab on iPhone. Anything the
       reader can see still animates; anything past the edge is fully freed. */
    }, { rootMargin: document.documentElement.classList.contains("lb-webkit")
           ? "10% 0px 10% 0px" : "45% 0px 45% 0px" });

    boxMap.forEach(function (list, box) { ioWide.observe(box); });
  }

  /* ------------------------------------------------------------------ */
  function initAll() {
    initHosts();
    initSwitch();
    initSpin();
    initSkewer();
    initHeroTap();
    initStack();
    initTurn();
    initCycle();
    initReveal();
    initSleep();
  }

  ready(initAll);
  /* the theme editor swaps section markup without a page load */
  document.addEventListener("shopify:section:load", initAll);
})();

/* ============================================================
   The Claude Design envelope & letter component, JS VERBATIM
   ("Lunch-B Envelope (new)"). It drives .lb-stage on its own —
   including the close-on-second-tap, which used to be our patch.
   ============================================================ */
(function () {
  'use strict';
  var RM = window.matchMedia ? matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var BEATS = [900, 1900, 3200]; // is-cut, is-lifting, is-open — timer floor, never event-driven

  function init(stage) {
    if (!stage || stage.__lbInit) return;
    stage.__lbInit = true;
    var opened = false, running = false, sx = 0, sy = 0, moved = true;

    stage.addEventListener('pointerdown', function (e) {
      sx = e.clientX; sy = e.clientY; moved = false;
    });
    stage.addEventListener('pointermove', function (e) {
      if (Math.abs(e.clientX - sx) > 12 || Math.abs(e.clientY - sy) > 12) moved = true;
    });
    stage.addEventListener('pointercancel', function () { moved = true; });
    stage.addEventListener('pointerup', function () {
      if (moved || running) return;
      var f = RM.matches ? 0.45 : 1;
      if (opened) { // reverse: tuck the letter back in, close the flap, reseal
        running = true;
        stage.classList.remove('is-open', 'is-lifting');
        stage.classList.add('is-closing');
        setTimeout(function () { stage.classList.remove('is-cut'); }, 850 * f);
        setTimeout(function () {
          stage.classList.remove('is-cutting', 'is-closing');
          opened = false; running = false;
        }, 2150 * f);
        return;
      }
      running = true;
      stage.classList.add('is-cutting');
      setTimeout(function () { stage.classList.add('is-cut'); }, BEATS[0] * f);
      setTimeout(function () { stage.classList.add('is-lifting'); }, BEATS[1] * f);
      setTimeout(function () {
        stage.classList.add('is-open');
        opened = true; running = false;
      }, BEATS[2] * f);
    });
    stage.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); moved = false;
        stage.dispatchEvent(new Event('pointerup')); }
    });
  }

  function boot(root) {
    var list = (root || document).querySelectorAll('.lb-stage');
    for (var i = 0; i < list.length; i++) init(list[i]);
  }
  window.LBEnvelope = { init: init, boot: boot };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
})();
